import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { platformApi, User as LegacyUser } from '@/services/platformApi';
import pubSubService from '@/services/pubsubService';
import { guestUserService } from '@/services/guestUserService';
import { User } from '@/models/User';
import { UserFactory } from '@/models/UserFactory';
import { UserApiAdapter } from '@/models/adapters/UserApiAdapter';
import { resolveSharedAuthToken } from '@vaiu/utils';

interface GuestData {
  username: string;
  userId: string;
  sessionExpiry: string;
}

interface AuthContextType {
  user: LegacyUser | null; // Keep legacy for compatibility
  userModel: User | null; // New OOP model
  loading: boolean;
  error: string | null;
  login: (token: string, isGuestLogin?: boolean, guestData?: GuestData) => Promise<void>;
  logout: () => void;
  fetchProfile: (token: string) => Promise<LegacyUser>;
  updateBalances: (balances: { [key: string]: number }) => void; // Update balances without full profile refresh
  scannedQrResult: string;
  setScannedQrResult: (data: string) => void;
  isGuest: boolean; // Helper to check if current user is a guest
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Filled by AuthNavigationSync (mounted inside BrowserRouter) so AuthProvider can sit
 * above the router without calling useNavigate.
 */
export const authNavigationRef = {
  current: null as null | ((to: string) => void),
};

export function AuthNavigationSync() {
  const navigate = useNavigate();
  useLayoutEffect(() => {
    authNavigationRef.current = (to: string) => navigate(to);
    return () => {
      authNavigationRef.current = null;
    };
  }, [navigate]);
  return null;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<LegacyUser | null>(null);
  const [userModel, setUserModel] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scannedQrResult, setScannedQrResult] = useState('');

  // Preload commonly used currencies
  const preloadCurrencies = async () => {
    try {
      // Default to common currencies if API call fails
      const commonCurrencies = ['USD', 'EUR', 'GBP', 'CAD'];
      
      try {
        // Try to get currencies from API, but don't block on failure
        const acceptableCurrencies = await platformApi.getAcceptableCurrencies();
        if (acceptableCurrencies?.all) {
          commonCurrencies.push(...acceptableCurrencies.all);
        }
      } catch (apiError) {
        console.warn('🏦 Failed to fetch acceptable currencies, using defaults:', apiError);
      }
      
      // Deduplicate currencies
      const uniqueCurrencies = [...new Set(commonCurrencies)];
      console.log('🏦 Preloading currencies:', uniqueCurrencies);
      
      try {
        await platformApi.preloadCurrencies(uniqueCurrencies);
        console.log('🏦 Currency preloading completed');
      } catch (preloadError) {
        console.warn('🏦 Failed to preload currencies:', preloadError);
      }

    } catch (error) {
      console.warn('🏦 Currency initialization failed:', error);
      // Don't block user experience if currency preloading fails
    }
  };

  const fetchProfile = useCallback(async (token: string) => {
    setLoading(true);
    setError(null);
    try {
      // Set token and fetch user profile
      platformApi.setToken(token);
      const profileData = await platformApi.getProfile();
      setUser(profileData);
      
      // Create OOP User model from the profile data
      const userModelInstance = UserApiAdapter.fromApiResponse(profileData);
      setUserModel(userModelInstance);
      
      // Preload currencies after successful login
      await preloadCurrencies();
      
      return profileData;
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      
      // Don't automatically remove tokens on any API errors
      // Let the user decide when to logout or refresh their session
      // This prevents silent logout due to temporary issues or permission problems
      console.warn('🚨 Profile fetch failed, but keeping user logged in:', error);
      
      setError(error instanceof Error ? error.message : 'Failed to fetch profile');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (token: string, isGuestLogin: boolean = false, guestData?: GuestData) => {
    try {
      // Handle guest users with the dedicated service
      if (isGuestLogin || guestUserService.isGuestToken(token)) {
        console.log('🔐 Guest login detected');
        platformApi.setToken(token);
        
        // Store token in localStorage for persistence
        localStorage.setItem('authToken', token);
        
        // Store guest session data if provided
        if (guestData) {
          guestUserService.storeGuestSession({
            ...guestData,
            token
          });
        }
        
        // Fetch guest profile using the service (handles fallbacks)
        const guestProfile = await guestUserService.fetchGuestProfile(token, guestData ? {
          ...guestData,
          token
        } : undefined);
        
        setUser(guestProfile);
        
        // Create OOP User model for guest
        const guestUserModel = UserApiAdapter.fromApiResponse(guestProfile);
        setUserModel(guestUserModel);
        
        setLoading(false);
        
        // Publish login event for guest users
        await pubSubService.publishUserLogin('guest', true);
        
        console.log('🔐 Guest user logged in:', guestProfile.username);
        
        // Schedule profile refresh attempts in the background
        // This won't block the user experience
        const scheduleProfileRefresh = () => {
          setTimeout(async () => {
            try {
              const updatedProfile = await guestUserService.fetchGuestProfile(token);
              setUser(updatedProfile);
              console.log('🔐 Guest profile refreshed:', updatedProfile.username);
            } catch (error) {
              console.debug('Profile refresh attempt failed, will retry');
              // Schedule another attempt if session hasn't expired
              if (!guestUserService.getSessionTimeRemaining().expired) {
                scheduleProfileRefresh();
              }
            }
          }, 5000); // Try after 5 seconds
        };
        
        scheduleProfileRefresh();
        return;
      }
      
      // Regular login flow — clear any prior guest session
      guestUserService.clearGuestData();
      const profile = await fetchProfile(token);
      
      // Store token in localStorage for regular login too
      localStorage.setItem('authToken', token);
      
      // Publish login event using the correct method
      await pubSubService.publishUserLogin('token', true);
      
      console.log('🔐 User logged in:', profile.username);
    } catch (error) {
      console.error('Login failed during initial authentication:', error);
      // Only clear token on explicit login failure (not subsequent API calls)
      console.warn('🔐 Removing token due to login failure');
      platformApi.removeToken();
      throw error;
    }
  }, [fetchProfile]);

  const updateBalances = useCallback((balances: { [key: string]: number }) => {
    setUser((currentUser) => {
      if (currentUser) {
        // Update balances in user object without full profile refresh
        const updatedUser = {
          ...currentUser,
          balances: balances
        };
        
        // Update userModel using the updated user data
        // We need to do this synchronously to ensure consistency
        setUserModel((currentUserModel) => {
          if (currentUserModel) {
            // Update balances in the OOP model by recreating from updated user data
            const updatedUserModel = UserApiAdapter.fromApiResponse({
              ...currentUser,
              balances: balances
            });
            return updatedUserModel;
          }
          return currentUserModel;
        });
        
        console.log('💰 [AuthContext] Balances updated:', balances);
        return updatedUser;
      }
      return currentUser;
    });
  }, []); // No dependencies - uses functional updates to access current state

  const logout = useCallback(() => {
    try {
      console.log('🔐 Explicit logout triggered by user action');
      
      // API logout call
      platformApi.logout().catch(error => {
        console.warn('Logout API call failed:', error);
        // Don't prevent local logout if API call fails
      });
      
      // Clear local state
      setUser(null);
      setUserModel(null);
      setError(null);
      platformApi.removeToken();
      
      // Clear currency cache
      platformApi.clearCurrencyCache();
      
      // Publish logout event using the correct method
      pubSubService.publishUserLogout();
      
      authNavigationRef.current?.('/login') ?? window.location.assign('/login');
      console.log('🔐 User explicitly logged out');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  // Check for existing token on app start
  useEffect(() => {
    const initializeAuth = async () => {
      resolveSharedAuthToken();
      const existingToken = localStorage.getItem('authToken');
      if (existingToken) {
        try {
          console.log('🔐 Found existing token, checking if guest...');
          
          // Check if it's a guest token
          if (guestUserService.isGuestToken(existingToken)) {
            console.log('🔐 Guest token detected, using guest service');
            
            // Check if session has expired
            const timeRemaining = guestUserService.getSessionTimeRemaining();
            if (timeRemaining.expired) {
              console.warn('🔐 Guest session has expired');
              platformApi.removeToken();
              guestUserService.clearGuestData();
              setLoading(false);
              return;
            }
            
            // Load guest profile
            platformApi.setToken(existingToken);
            const guestProfile = await guestUserService.fetchGuestProfile(existingToken);
            setUser(guestProfile);
            console.log('🔐 Guest user restored:', guestProfile.username);
            setLoading(false);  // Important: Set loading to false after successful restore
          } else {
            // Regular user token
            await fetchProfile(existingToken);
          }
        } catch (error) {
          console.warn('🔐 Failed to validate existing token:', error);
          // Don't automatically remove token here - fetchProfile handles authentication errors
          // This prevents silent logout when there are temporary network issues or API problems
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Memoize setScannedQrResult to keep it stable
  const setScannedQrResultMemo = useCallback((result: string) => {
    setScannedQrResult(result);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  // and ensure the context is always available
  const value = useMemo(() => ({
    user,
    userModel,
    loading,
    error,
    login,
    logout,
    fetchProfile,
    updateBalances,
    scannedQrResult,
    setScannedQrResult: setScannedQrResultMemo,
    isGuest: user?.isGuest || false
  }), [user, userModel, loading, error, scannedQrResult, updateBalances, login, logout, fetchProfile, setScannedQrResultMemo]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 